<template>
    <div class="bg-gray-700 min-h-screen">
        <div class="max-w-lg mx-auto py-2">
            <AppCard>
                <div class="flex gap-x-2">
                    <h1 class="text-2xl font-semibold">
                        <SmartBreadcrumbs
                            type="InertiaLink"
                            :pages="[
                                {
                                    name: $tc('messages.itineraries', 2),
                                    href: meta.links.index,
                                },
                                {
                                    name: itinerary.name,
                                    href: null,
                                },
                            ]"
                        />
                    </h1>

                    <button v-if="itinerary.description">
                        <IconInfoCircle @click="toggleDescription()" class="h-6 w-6 text-gray-400" />
                    </button>
                </div>
            </AppCard>
            <transition
                enter-active-class="transition ease-linear duration-200"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-linear duration-150"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <AppCard class="mt-3 p-2" v-if="showDescription">
                    <p class="text-sm text-gray-700 whitespace-pre-line">{{ itinerary.description }}</p>
                </AppCard>
            </transition>
            <transition
                enter-active-class="transition ease-linear duration-200"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-linear duration-150"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <AlertMessage class="mt-3" type="info" v-if="itinerary.working_schedule">
                    <template v-slot:title>
                        {{ itinerary.working_schedule }}
                    </template>
                </AlertMessage>
            </transition>
        </div>
        <div
            class="max-w-lg mx-auto left-0 top-0 min-h-full rounded-xl relative"
            style="background-repeat: repeat-y; background-size: 100% auto"
            :style="`background-image: url(${itinerary.background_image_path})`"
        >
            <div v-for="station in stations" class="grid grid-cols-5 list-group-item" :key="station.id">
                <div style="order: 1">&nbsp;</div>
                <div style="order: 2">&nbsp;</div>
                <div style="order: 3">&nbsp;</div>
                <div style="order: 4">&nbsp;</div>
                <div
                    class="aspect-square inline-block drop-shadow-xl"
                    :style="`order: ${station.position}`"
                    :id="`station_${station.id}`"
                >
                    <div class="w-full h-full" v-if="station.type === 'gamification_chest'">
                        <Tippy v-if="station.is_locked">
                            <TreasureChest
                                :type="station.settings.type"
                                class="object-contain"
                                :is-open="!!station.completed_at"
                                :class="{ 'saturate-0': station.is_locked, 'cursor-pointer': !station.is_locked }"
                            />
                            <template #content>
                                <div>
                                    {{ $t('messages.is_locked') }}
                                </div>
                                <div v-if="station.unlocked_at_message">
                                    <div>
                                        {{ station.unlocked_at_message.diff_for_humans }}
                                    </div>
                                    <div v-if="station.unlocked_at_message.date">
                                        ({{ station.unlocked_at_message.date }})
                                    </div>
                                </div>
                            </template>
                        </Tippy>
                        <TreasureChest
                            v-else
                            :type="station.settings.type"
                            class="object-contain"
                            :is-open="!!station.completed_at"
                            :class="{ 'saturate-0': station.is_locked, 'cursor-pointer': !station.is_locked }"
                            @click="openStationWhenUnlocked(station)"
                        />
                    </div>
                    <div class="w-full h-full" v-else-if="station.type === 'final'">
                        <Tippy v-if="station.is_locked">
                            <img
                                class="w-full h-full object-contain"
                                :src="station.image"
                                :class="{ 'saturate-0': station.is_locked, 'cursor-pointer': !station.is_locked }"
                                alt=""
                            />
                            <template #content>
                                <div>
                                    {{ $t('messages.is_locked') }}
                                </div>
                                <div v-if="station.unlocked_at_message">
                                    <div>
                                        {{ station.unlocked_at_message.diff_for_humans }}
                                    </div>
                                    <div v-if="station.unlocked_at_message.date">
                                        ({{ station.unlocked_at_message.date }})
                                    </div>
                                </div>
                            </template>
                        </Tippy>
                        <img
                            v-else
                            class="w-full h-full object-contain"
                            :src="station.image"
                            :class="{ 'saturate-0': station.is_locked, 'cursor-pointer': !station.is_locked }"
                            alt=""
                            @click="openFinalStation(station)"
                        />
                    </div>
                    <StationBase
                        v-else
                        :color="itinerary.base_color"
                        :shape="itinerary.base_shape"
                        style="transform: rotateX(45deg); transform-style: preserve-3d"
                        with-shadow
                        :is-completed="!!station.completed_at"
                        :class="{ 'saturate-0': station.is_locked }"
                    >
                        <Tippy v-if="station.is_locked" class="w-1/2 h-1/2">
                            <StationIcon :type="station.type" class="w-full h-full text-white opacity-50" />
                            <template #content>
                                <div>
                                    {{ $t('messages.is_locked') }}
                                </div>
                                <div v-if="station.unlocked_at_message">
                                    <div>
                                        {{ station.unlocked_at_message.diff_for_humans }}
                                    </div>
                                    <div v-if="station.unlocked_at_message.date">
                                        ({{ station.unlocked_at_message.date }})
                                    </div>
                                </div>
                                <div v-if="itinerary.working_schedule">({{ itinerary.working_schedule }})</div>
                            </template>
                        </Tippy>
                        <StationIcon
                            v-else
                            :type="station.type"
                            class="w-1/2 h-1/2 text-white opacity-50 cursor-pointer"
                            @click="openStation(station)"
                        />
                    </StationBase>
                </div>
            </div>
            <StudentStationAvatar
                :avatar="student.avatar_or_default"
                :costume="student.costume"
                :index="index"
                class="w-1/5 aspect-square absolute top-0 left-0"
            />
            <StudentStationPet
                :avatar="student.pet.image_path"
                v-if="student.pet"
                :index="index"
                class="w-1/5 aspect-square absolute top-0 left-0"
            />
        </div>
    </div>
    <StudentStationPanel v-if="station" :station="station" @close="closeStation" />
    <FinalStationModal v-if="finalStation" :station="finalStation" @close="closeStation" />
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import StationBase from '@/components/Forms/StationBase.vue';
import AppCard from '@/Shared/AppCard.vue';
import StationIcon from '@/components/Forms/StationIcon.vue';
import { Tippy } from 'vue-tippy';
import StudentStationPanel from '@/components/pageSpecific/StudentStationPanel.vue';
import TreasureChest from '@/components/TreasureChest.vue';
import { IconInfoCircle } from '@tabler/icons-vue';
import AlertMessage from '@/components/AlertMessage.vue';
import FinalStationModal from '@/components/pageSpecific/FinalStationModal.vue';
import StudentStationAvatar from '@/components/pageSpecific/StudentStationAvatar.vue';
import StudentStationPet from '@/components/pageSpecific/StudentStationPet.vue';

export default {
    components: {
        StudentStationPet,
        StudentStationAvatar,
        FinalStationModal,
        AlertMessage,
        TreasureChest,
        StudentStationPanel,
        StationIcon,
        StationBase,
        AppCard,
        Tippy,
        IconInfoCircle,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'gamificationSet', 'student', 'itinerary'],
    data() {
        return {
            stations: this.itinerary.stations,
            station: null,
            finalStation: null,
            index: this.student.index,
            showDescription: this.itinerary.description && this.itinerary.completed_count <= 3,
        };
    },
    mounted() {
        this.scrollScreen();
    },
    methods: {
        scrollScreen() {
            const currentStationId = this.itinerary.current_station_id;
            if (currentStationId === null) {
                return;
            }
            const id = `station_` + currentStationId;
            const elementById = document.getElementById(id);
            if (!elementById) {
                return;
            }
            setTimeout(() => {
                elementById.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 1000);
        },
        toggleDescription() {
            this.showDescription = !this.showDescription;
        },
        openStation(station) {
            this.moveCharacter(station);
            this.station = station;
        },
        openStationWhenUnlocked(station) {
            if (!station.is_locked) {
                this.openStation(station);
            }
        },
        openFinalStation(station) {
            if (!station.is_locked) {
                this.finalStation = station;
            }
        },
        closeStation(isCompleted) {
            this.station = null;
            this.finalStation = null;
            if (isCompleted) {
                this.$inertia.get(
                    this.meta.links.show,
                    {},
                    {
                        preserveState: true,
                        preserveScroll: true,
                        onSuccess: () => {
                            this.stations = this.itinerary.stations;
                            this.index = this.student.index;
                        },
                    },
                );
            }
        },
        moveCharacter(station) {
            this.index = station.index;
        },
    },
};
</script>
